'use client';

import { motion } from 'framer-motion';
import { Star, Award, Users, Globe, Shield, Heart, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function AboutPage() {
  const values = [
    {
      icon: <Heart className="w-8 h-8 text-[#FFC400]" />,
      title: "Guest-Centric",
      description: "Every decision we make is focused on creating exceptional experiences for our guests."
    },
    {
      icon: <Shield className="w-8 h-8 text-[#FFC400]" />,
      title: "Trust & Security",
      description: "We maintain the highest standards of safety, security, and reliability in all our properties."
    },
    {
      icon: <Globe className="w-8 h-8 text-[#FFC400]" />,
      title: "Sustainability",
      description: "Committed to responsible tourism and environmental stewardship in all our operations."
    },
    {
      icon: <Award className="w-8 h-8 text-[#FFC400]" />,
      title: "Excellence",
      description: "We strive for excellence in every detail, from property selection to guest service."
    }
  ];

  const achievements = [
    { number: "500+", label: "Happy Guests" },
    { number: "50+", label: "Luxury Properties" },
    { number: "4.9/5", label: "Average Rating" },
    { number: "24/7", label: "Guest Support" }
  ];

  const team = [
    {
      name: "Mohamed Rafik ",
      role: "Founder & CEO",
      image: "/images/mohammadrafik.jpg.jpeg",
      description: "With a background in private banking and over a decade of experience in finance and real estate, Mr. Mohamed Rafik has built 18Bricks Holiday Homes into one of Dubai's most dynamic and results-driven Holiday Homes Company."
    },
    {
      name: "Sharon Rosell",
      role: "Operations and Revenue Manager",
      image: "/images/sharon.jpg.jpeg",
      description: "With over a decade of experience in the holiday homes industry, complemented by a strong background in real estate, Sharon ensures the seamless operation of our Holiday Homes department."
    },
    {
      name: "Brian Samuel",
      role: "Administrator/Sales Executive",
      image: "/images/brian.jpg.jpeg",
      description: "Brian is an integral part of the 18Bricks team, handling administrative tasks and supporting sales efforts with efficiency and a client-focused approach."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden h-[50vh]">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-secondary/60 z-10"></div>
          <img
            src="/images/dubai-marina.jpg"
            alt="Dubai Marina"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="relative h-full z-20 max-w-7xl mx-auto flex items-center justify-center text-center">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About 18Bricks
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              We're passionate about creating extraordinary vacation experiences through
              carefully curated luxury properties and exceptional service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Our Story</h2>
              <p className="text-lg text-gray-600 mb-6">
                Founded in 2020, 18Bricks began as a vision to transform the way people experience
                luxury vacations. What started as a small collection of carefully selected properties
                has grown into a premier platform connecting discerning travelers with extraordinary
                accommodations.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                We believe that every vacation should be memorable, every property should exceed
                expectations, and every guest should feel valued. Our name "18Bricks" represents
                the foundation we build each experience upon - solid, reliable, and crafted with care.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {achievements.map((achievement, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-[#FFC400] mb-2">{achievement.number}</div>
                    <p className="text-sm text-gray-600">{achievement.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="relative"
            >
              <Image
                src="/images/Aboutus.jpeg"
                alt="18Bricks Office"
                width={600}
                height={400}
                className="rounded-lg shadow-2xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32">
                <Image
                  src="/images/logo.jpg"
                  alt="18Bricks Logo"
                  width={128}
                  height={128}
                  className="w-full h-full object-cover rounded-lg shadow-lg"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Our Mission & Vision</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We're driven by a simple yet powerful mission: to create unforgettable experiences
              that connect people with the world's most beautiful places.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <Card className="h-full shadow-xl border-0">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#FFC400]/10 rounded-full flex items-center justify-center mb-6">
                    <Heart className="w-8 h-8 text-[#FFC400]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">Our Mission</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    To provide exceptional vacation experiences through carefully curated luxury properties,
                    personalized service, and unwavering attention to detail. We're committed to making
                    every guest feel special and every stay memorable.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <Card className="h-full shadow-xl border-0">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#FFC400]/10 rounded-full flex items-center justify-center mb-6">
                    <Globe className="w-8 h-8 text-[#FFC400]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">Our Vision</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    To become the world's most trusted platform for luxury vacation rentals,
                    known for our exceptional properties, outstanding service, and innovative
                    approach to hospitality. We envision a world where every vacation is extraordinary.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Our Values</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              These core values guide everything we do and shape the experiences we create for our guests.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="w-20 h-20 bg-[#FFC400]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Meet Our Team</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our dedicated team of hospitality professionals works tirelessly to ensure
              every guest has an exceptional experience.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {team.map((member, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
              >
                <Card className="shadow-xl border-0 min-h-[520] overflow-hidden">
                  <div className="h-64 bg-gray-200 flex items-center justify-center">
                    <Image src={member.image} alt="" width={400} height={400} />
                  </div>
                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{member.name}</h3>
                    <Badge variant="secondary" className="mb-4 bg-[#FFC400]/10 text-[#FFC400] border-[#FFC400]/20">
                      {member.role}
                    </Badge>
                    <p className="text-gray-600 ">{member.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Why Choose 18Bricks?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We go above and beyond to ensure your vacation is perfect in every way.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                icon: <CheckCircle className="w-8 h-8 text-[#FFC400]" />,
                title: "Verified Properties",
                description: "Every property undergoes rigorous quality checks to ensure it meets our high standards."
              },
              {
                icon: <Clock className="w-8 h-8 text-[#FFC400]" />,
                title: "24/7 Support",
                description: "Our dedicated support team is available around the clock to assist with any needs."
              },
              {
                icon: <Award className="w-8 h-8 text-[#FFC400]" />,
                title: "Premium Service",
                description: "From booking to checkout, we provide white-glove service at every touchpoint."
              },
              {
                icon: <Shield className="w-8 h-8 text-[#FFC400]" />,
                title: "Secure Booking",
                description: "Your personal information and payments are protected with bank-level security."
              },
              {
                icon: <TrendingUp className="w-8 h-8 text-[#FFC400]" />,
                title: "Best Rates",
                description: "We negotiate exclusive rates with property owners to offer you the best value."
              },
              {
                icon: <Heart className="w-8 h-8 text-[#FFC400]" />,
                title: "Guest Satisfaction",
                description: "We're committed to exceeding expectations and creating lasting memories."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#FFC400]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FFC400] to-[#FFA500]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">
              Ready to Experience the 18Bricks Difference?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Discover our collection of luxury properties and start planning your perfect getaway today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/listings"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#FFC400] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                View Properties
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-[#FFC400] transition-colors"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
